import React, { useRef, useEffect, useState, useCallback } from 'react';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';

/**
 * Intersection-Observer-based scroll reveal.
 * Ported from glenn-home/app/hooks/useScrollReveal — adapted for SCSS modules.
 *
 * Variants:
 * - 'up'    → Fade up from 30px below          (default)
 * - 'left'  → Slide in from 40px left
 * - 'right' → Slide in from 40px right
 * - 'scale' → Scale up from 0.92
 */
export type RevealVariant = 'up' | 'left' | 'right' | 'scale';

interface ScrollRevealProps {
  children: React.ReactNode;
  /** Animation variant (default: 'up') */
  variant?: RevealVariant;
  /** Extra transition-delay in seconds */
  delay?: number;
  /** CSS class to merge on the wrapper div */
  className?: string;
  /** Wrapper element tag (default: 'div') */
  as?: keyof JSX.IntrinsicElements;
}

const VARIANT_MAP: Record<RevealVariant, string> = {
  up: 'reveal-up',
  left: 'reveal-left',
  right: 'reveal-right',
  scale: 'reveal-scale',
};

export default function ScrollReveal({
  children,
  variant = 'up',
  delay = 0,
  className,
  as: Tag = 'div',
}: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return React.createElement(
    Tag,
    {
      ref,
      className: buildClassName(
        styles[VARIANT_MAP[variant]],
        revealed && styles.revealed,
        className
      ),
      style: delay ? { transitionDelay: `${delay}s` } : undefined,
    },
    children
  );
}

// ─── Stagger variant ────────────────────────────────────────
// Applies incremental reveal delay to each child element.

interface StaggerRevealProps {
  children: React.ReactNode;
  /** Delay increment per child in seconds (default: 0.06) */
  staggerDelay?: number;
  className?: string;
}

export function StaggerReveal({
  children,
  staggerDelay = 0.06,
  className,
}: StaggerRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={buildClassName(
        styles['stagger-container'],
        revealed && styles['stagger-revealed'],
        className
      )}
    >
      {React.Children.map(children, (child, i) =>
        React.isValidElement(child)
          ? React.cloneElement(child as React.ReactElement<any>, {
            style: {
              ...(child.props.style || {}),
              transitionDelay: `${i * staggerDelay}s`,
            },
          })
          : child
      )}
    </div>
  );
}

// ─── Animated counter ───────────────────────────────────────
// Counts up a number with easeOutCubic when the element scrolls into view.
// Ported from glenn-home/app/hooks/useAnimatedCounter.

interface AnimatedCounterProps {
  end: number;
  /** Duration in ms (default: 1600) */
  duration?: number;
  /** Delay before counting starts in ms (default: 0) */
  delay?: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  className?: string;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

export function AnimatedCounter({
  end,
  duration = 1600,
  delay = 0,
  prefix = '',
  suffix = '',
  decimals = 0,
  className,
}: AnimatedCounterProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [display, setDisplay] = useState(`${prefix}0${suffix}`);
  const hasAnimated = useRef(false);

  const animate = useCallback(() => {
    const startTime = performance.now() + delay;
    let rafId: number;

    function tick(now: number) {
      const elapsed = now - startTime;
      if (elapsed < 0) {
        rafId = requestAnimationFrame(tick);
        return;
      }
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);
      const current = eased * end;
      const formatted = decimals > 0
        ? current.toFixed(decimals)
        : Math.round(current).toLocaleString();
      setDisplay(`${prefix}${formatted}${suffix}`);
      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      }
    }

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [end, duration, delay, prefix, suffix, decimals]);

  // Reset animation when the target value changes so the counter re-animates
  const prevEndRef = useRef(end);
  useEffect(() => {
    if (prevEndRef.current !== end) {
      prevEndRef.current = end;
      hasAnimated.current = false;
    }
  }, [end]);

  useEffect(() => {
    const el = ref.current;
    if (!el || hasAnimated.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          animate();
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [animate]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
}

// ─── Progress fill bar ──────────────────────────────────────
// Animates width from 0 to target percentage when scrolled into view.

interface ProgressFillProps {
  percentage: number;
  className?: string;
  activeClassName?: string;
}

export function ProgressFill({ percentage, className, activeClassName }: ProgressFillProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={buildClassName(className, activeClassName)}
      style={{ width: revealed ? `${percentage}%` : '0%' }}
    />
  );
}
